/* eslint-disable @typescript-eslint/camelcase */
import {
	required,
	email,
	integer,
	numeric,
	max,
	min,
	max_value,
	min_value,
	between,
	is,
	oneOf,
	confirmed,
	double,
	length,
	digits,
} from 'vee-validate/dist/rules';
import { extend, configure } from 'vee-validate';
import i18n from '../lib/i18n';
import axios from 'axios';
import { isValid, parse } from 'date-fns';
import store from '../store';
import { LocaleCode } from '../types';
import { ProfileGetterKeys } from '../store/modules/profile';

const DEFAULT_DATE_FORMAT = 'yyyy-MM-dd';
const DEFAULT_TIME_FORMAT = 'hh:mma';

configure({
	defaultMessage: (field, values) => {
		// TODO: i18n field name
		// values._field_ = i18n.t(`fields.${field}`);

		// change name to use default validation error msg
		if (values._rule_ == 'minValue') {
			values._rule_ = 'min_value';
		}
		if (values._rule_ == 'maxValue') {
			values._rule_ = 'max_value';
		}

		if (
			values._rule_ == 'dropdownRequired' ||
			values._rule_ == 'arrayAtLeastOne'
		) {
			values._rule_ = 'required';
		}

		values._field_ = i18n.t('validation.field'); // TODO: i18n validation

		return i18n.t(`validation.${values._rule_}`, values).toString();
	},
});

extend('required', required);

extend('integer', integer);

extend('numeric', numeric);

extend('length', length);

extend('double', double);

extend('max', max);

extend('min', min);

extend('maxValue', max_value);

extend('minValue', min_value);

extend('between', between);

extend('email', email);

extend('oneOf', oneOf);

extend('is', is);

extend('confirmed', confirmed);

extend('digits', digits);

extend('dropdownRequired', {
	params: ['emptyValue'],
	validate(value, { emptyValue }: any) {
		return JSON.stringify(value) !== JSON.stringify(emptyValue);
	},
});

extend('date', {
	validate(value) {
		const dateFormat =
			store.getters[`profile/${ProfileGetterKeys.preferences}`].dateFormat ||
			DEFAULT_DATE_FORMAT;
		let result = true;
		try {
			result = isValid(parse(value, dateFormat, new Date()));
		} catch (e) {
			result = false;
		}
		return result;
	},
});

extend('time', {
	validate(value) {
		const timeFormat = DEFAULT_TIME_FORMAT;
		let result = true;
		try {
			result = isValid(
				parse('2023-01-01 ' + value, 'yyyy-MM-dd ' + timeFormat, new Date()),
			);
		} catch (e) {
			result = false;
		}
		return result;
	},
});

extend('minChoices', {
	params: ['min'],
	validate(value, { min }: any) {
		return value.length >= min;
	},
});

extend('maxChoices', {
	params: ['max'],
	validate(value, { max }: any) {
		return value.length <= max;
	},
});

extend('greaterThan', {
	params: ['amount'],
	validate(value, { amount }: any) {
		return value > amount;
	},
});

extend('unique', {
	params: ['checkingEndpoint', 'currentValue', 'customErrorMessage'],
	async validate(
		value,
		{ checkingEndpoint, currentValue, customErrorMessage }: any,
	) {
		if (!!currentValue && value == currentValue) {
			return true;
		}
		const { data: result } = await axios.get(`${checkingEndpoint}${value}`);
		if (result && customErrorMessage) {
			return customErrorMessage;
		}
		return !result;
	},
});

const includes = (collection: any[] | string, item: string): boolean => {
	return collection.indexOf(item) !== -1;
};

const testEmpty = (value: string) =>
	includes([null, undefined], value) || !String(value).trim().length;

// for locales
extend('atLeastOne', {
	params: [LocaleCode.EN, LocaleCode.TC, LocaleCode.SC],
	computesRequired: true,
	validate(
		value,
		{
			[LocaleCode.EN]: en,
			[LocaleCode.TC]: zhHant,
			[LocaleCode.SC]: zhHans,
		}: any,
	) {
		return {
			required: true,
			valid: !(
				testEmpty(value) &&
				testEmpty(en) &&
				testEmpty(zhHant) &&
				testEmpty(zhHans)
			),
		};
	},
});

extend('arrayAtLeastOne', {
	computesRequired: true,
	validate(value: any[]) {
		return value && value.length >= 1;
	},
});

extend('isUrl', {
	validate(value) {
		let url;

		try {
			url = new URL(value);
		} catch (_) {
			return false;
		}

		// from https://stackoverflow.com/a/5717133/6228451
		const pattern = new RegExp(
			'^(https?:\\/\\/)?' + // protocol
			'((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
			'((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
			'(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
			'(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
				'(\\#[-a-z\\d_]*)?$',
			'i',
		); // fragment locator
		return pattern.test(value);
	},
});

extend('mixUpperLowerCase', {
	validate(value) {
		const passwordRule = new RegExp('^(?=.*[a-z])(?=.*[A-Z])');
		return {
			valid: passwordRule.test(value),
		};
	},
});

extend('uuid', {
	validate(value) {
		const uuidRule = new RegExp(
			/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i,
		);
		return {
			valid: uuidRule.test(value),
		};
	},
});

extend('mixAlphaNumber', {
	validate(value) {
		const passwordRule = new RegExp('^(?=.*[a-zA-Z])(?=.*[0-9])');
		return {
			valid: passwordRule.test(value),
		};
	},
});

extend('mixAlphaNumberSpecial', {
	validate(value) {
		// TODO: not yet handle "[", "]" these two special character
		const passwordRule = new RegExp(
			'^(?=.*[a-zA-Z])(?=.*[0-9])(?=.*[!@#$%^&*+\\-\\[\\]\'"/.,<>?()_{}~=`])[a-zA-Z0-9!@#$%^&*+\\-\\[\\]\'"/.,<>?()_|{}~=`]+$',
		);
		return {
			valid: passwordRule.test(value),
		};
	},
});

extend('startEndDate', {
	params: ['startDate', 'endDate', 'allowSameDate'],
	validate(_, { startDate, endDate, allowSameDate }: any) {
		if (!startDate || !endDate) return true;

		const startDateObj = new Date(startDate),
			endDateObj = new Date(endDate);

		if (
			!allowSameDate &&
			startDateObj.toJSON().slice(0, 10) === endDateObj.toJSON().slice(0, 10)
		) {
			return i18n.t('pages.jobs.schedule.errors.datesEqual').toString();
		}

		if (startDateObj > endDateObj)
			return i18n
				.t('pages.jobs.schedule.errors.startDateNotEarlier')
				.toString();
		else return true;
	},
});

// TODO: remove unnecessary code, startEndDate covered this
extend('startDateEarlierThanEndDate', {
	params: ['startDate', 'endDate'],
	validate(_, { startDate, endDate }: any) {
		if (!startDate || !endDate) return true;

		const startDateObj = new Date(startDate),
			endDateObj = new Date(endDate);

		if (startDateObj > endDateObj)
			return i18n
				.t('pages.jobs.schedule.errors.startDateNotEarlier')
				.toString();
		else return true;
	},
});

extend('startEndTime', {
	params: ['startDate', 'endDate', 'startTime', 'endTime'],
	validate(_, { startDate, endDate, startTime, endTime }: any) {
		if (!startTime || !endTime) return true;
		const dummyDatePrefix = '2021-01-01';
		if (!startDate || !endDate) startDate = endDate = dummyDatePrefix;
		// unix timestamps in ms
		const startTimeMs = new Date(`${startDate}T${startTime}`).getTime(),
			endTimeMs = new Date(`${endDate}T${endTime}`).getTime();

		if (startTimeMs === endTimeMs)
			return i18n.t('pages.jobs.schedule.errors.timesEqual').toString();
		if (startTimeMs > endTimeMs)
			return i18n
				.t('pages.jobs.schedule.errors.startTimeNotEarlier')
				.toString();
		else return true;
	},
});

extend('beforeDate', {
	params: ['currentDate', 'beforeDate', 'currentTime', 'beforeTime', 'i18nKey'],
	validate(
		_,
		{ currentDate, beforeDate, currentTime, beforeTime, i18nKey }: any,
	) {
		if (!currentTime || !beforeTime) {
			return true;
		}
		// unix timestamps in ms
		const startTimeMs = new Date(`${currentDate}T${currentTime}`).getTime(),
			endTimeMs = new Date(`${beforeDate}T${beforeTime}`).getTime();
		if (startTimeMs >= endTimeMs) {
			return i18n.t(i18nKey).toString();
		}
		return true;
	},
});

extend('exceptionDates', {
	params: ['startDate', 'endDate'],
	validate(dates: string[], { startDate, endDate }: any) {
		if (!startDate || !endDate || !dates?.length) return true;

		const startDateObj = new Date(startDate),
			endDateObj = new Date(endDate);

		if (
			dates.find(date => {
				// check if date is outside the period
				const exceptionDateObj = new Date(date);
				return exceptionDateObj < startDateObj || exceptionDateObj > endDateObj;
			})
		) {
			return i18n
				.t('pages.jobs.schedule.errors.exceptionDatesNotWithinPeriod')
				.toString();
		} else return true;
	},
});

extend('ngoCode', {
	validate(value) {
		const ngoCodeRule = new RegExp('^[A-Z]+[A-Z_0-9]*$');
		return {
			valid: ngoCodeRule.test(value),
		};
	},
});

extend('decimal', {
	params: ['decimals'],
	validate(value, { decimals, name }: any) {
		const decimalsRule = new RegExp(`^-?[0-9,]+\\.?[0-9]{0,${decimals}}$`);
		const valid = decimalsRule.test(value);
		if (valid) {
			return true;
		}

		const field = name; // TODO: cannot get the validator name, so if not manually set, let it be empty

		if (decimals === 1) {
			return i18n.t('validation.decimal1', { _field_: field }).toString();
		} else {
			return i18n
				.t('validation.decimal', { decimals, _field_: field })
				.toString();
		}
	},
});

extend('isAvailableOption', {
	params: ['isPublished', 'isDeleted'],
	validate(_, { isPublished, isDeleted }: any) {
		if (isPublished && isDeleted) return true;
		if (isDeleted) return false;
		return true;
	},
});

extend('commaSeparatedNumber', {
	params: [],
	validate(value) {
		const decimalsRule = new RegExp(`^-?[0-9,]+\\.?[0-9]*$`);
		return {
			valid: decimalsRule.test(value),
		};
	},
});

extend('commaSeparatedInteger', {
	params: [],
	validate(value) {
		const integerRule = new RegExp(`^-?[0-9,]+$`);
		return {
			valid: integerRule.test(value),
		};
	},
});

extend('stringNumberMin', {
	params: ['min'],
	validate(value, { min }: any) {
		return {
			valid:
				min == null ||
				value == null ||
				Number(String(value).replace(/,/g, '')) >=
					Number(min.replace(/,/g, '')),
		};
	},
});

extend('stringNumberMax', {
	params: ['max'],
	validate(value, { max }: any) {
		return {
			valid:
				max == null ||
				value == null ||
				Number(String(value).replace(/,/g, '')) <=
					Number(max.replace(/,/g, '')),
		};
	},
});

extend('customRule', {
	params: ['validate'],
	validate(_, { validate }: any) {
		return validate();
	},
});

extend('phoneNumber', {
	validate(value) {
		const phoneNumberRule = new RegExp(/^[0-9+\-()\s]*$/);
		return {
			valid: phoneNumberRule.test(value),
		};
	},
});

extend('emailDomain', {
	validate(domains: string[]) {
		// eslint-disable-next-line
		const domainsRule = new RegExp(/^\w+([\.-]?\w+)*(\.\w{2,4})+$/);
		const valid = domains.every(domain => domainsRule.test(domain));
		return valid || i18n.t('validation.emailDomain').toString();
	},
});

extend('domainValidation', {
	params: ['domains'],
	validate(value: string, { domains }: any) {
		const emailDomain = value.split('@').pop();
		const valid = domains.some((domain: string) => emailDomain === domain);
		return (
			valid ||
			i18n
				.t('validation.domainValidation', { domains: domains.join(', ') })
				.toString()
		);
	},
});

//used for checkbox
extend('checked', {
	validate(value: boolean) {
		return value === true || i18n.t('validation.mustBeChecked').toString();
	},
});

extend('programEnablerRules', {
	params: ['validate'],
	computesRequired: true,
	validate(_, { validate }: any) {
		return validate();
	},
});

// TODO: remove redundant code, identical to startDateEarlierThanEndDate
extend('endDateEarlierThanStartDate', {
	params: ['startDate', 'endDate'],
	validate(_, { startDate, endDate }: any) {
		if (!startDate || !endDate) return true;

		const startDateObj = new Date(startDate),
			endDateObj = new Date(endDate);

		if (startDateObj > endDateObj)
			return i18n.t('pages.jobs.schedule.errors.endDateNotEarlier').toString();
		else return true;
	},
});

extend('uniqueLeader', {
	params: ['pic', 'allOtherLeaders'],
	validate(_, { pic, allOtherLeaders }: any) {
		const leader = allOtherLeaders.slice(-1)[0];
		const otherLeaders = allOtherLeaders.slice(0, allOtherLeaders.length - 1);

		if ((pic && pic === leader) || otherLeaders.includes(leader)) {
			return i18n.t('validation.programFormUniqueLeader').toString();
		}
		return true;
	},
});

extend('usernameLength', {
	params: ['min', 'max', 'username'],
	validate(_, { min, max, username }: any) {
		if (username.length < min || username.length > max) {
			return i18n.t('validation.usernameLength').toString();
		}
		return true;
	},
});

extend('lowerCaseOnly', {
	params: ['email'],
	validate(_, { email }: any) {
		const [username, domain] = email.split('@');

		if (
			!/^[a-z]+$/.test(username) ||
			!/^[a-z]+$/.test(domain.replace(/\./g, ''))
		) {
			return i18n.t('validation.emailLowerCaseOnly').toString();
		}
		return true;
	},
});

extend('confirmEmail', {
	params: ['email', 'confirmEmail'],
	validate(_, { email, confirmEmail }: any) {
		if (email !== confirmEmail) {
			return i18n.t('validation.emailsNotMatch').toString();
		}
		return true;
	},
});
